import type { GitProvider } from '../config/providers';

/**
 * Application types for managing collections of Git repositories.
 */

/**
 * Context for navigating back to an application page from detail pages.
 * Used to enable "Back to [AppName]" instead of generic "Back to [List]".
 */
export interface FromApplicationContext {
  appId: string;
  appName: string;
}

export interface ApplicationRepository {
  full_name: string;
  default_branch: string;
  html_url?: string;
  provider?: GitProvider;
  provider_repository_id?: string;
  gitlab_instance_url?: string;
  is_public_demo_repo?: boolean;
  pr_scan_enabled?: boolean;
  pr_scan_mode?: PrScanMode;
  pr_scan_engines?: PrScanEngine[];
}

export type PrScanMode = 'partial' | 'full';

export type PrScanEngine = 'secrets' | 'semgrep' | 'ai';

// ===== Application Profile Types =====

export type ApplicationType =
  | 'web_app'
  | 'api_server'
  | 'cli_tool'
  | 'library'
  | 'background_worker'
  | 'mobile_backend';

export type DeploymentExposure = 'public_facing' | 'internal' | 'local_only';

export type InputSource =
  | 'http_requests'
  | 'cli_args'
  | 'file_input'
  | 'message_queue'
  | 'database'
  | 'internal_api';

export type AuthenticationModel =
  | 'none'
  | 'basic'
  | 'session'
  | 'token'
  | 'oauth'
  | 'service_to_service'
  | 'api_key';

export type DataSensitivity =
  | 'public'
  | 'internal'
  | 'confidential'
  | 'pii'
  | 'financial'
  | 'healthcare';

export type ProfileConfidence = 'high' | 'medium' | 'low';

export interface ApplicationProfile {
  application_type: ApplicationType;
  deployment_exposure: DeploymentExposure;
  input_sources: InputSource[];
  authentication_model: AuthenticationModel;
  data_sensitivity: DataSensitivity;
  detected_frameworks: string[];
  context_notes: string;
  confidence: ProfileConfidence;
  is_user_modified: boolean;
  derived_at?: string;
  modified_at?: string;
}

export interface ApplicationProfileUpdate {
  application_type?: ApplicationType;
  deployment_exposure?: DeploymentExposure;
  input_sources?: InputSource[];
  authentication_model?: AuthenticationModel;
  data_sensitivity?: DataSensitivity;
  context_notes?: string;
}

// Display labels for profile enums
export const APPLICATION_TYPE_LABELS: Record<ApplicationType, string> = {
  web_app: 'Web Application',
  api_server: 'API Server',
  cli_tool: 'CLI Tool',
  library: 'Library/SDK',
  background_worker: 'Background Worker',
  mobile_backend: 'Mobile Backend',
};

export const DEPLOYMENT_EXPOSURE_LABELS: Record<DeploymentExposure, string> = {
  public_facing: 'Public-Facing',
  internal: 'Internal/Corporate',
  local_only: 'Local Development Only',
};

export const INPUT_SOURCE_LABELS: Record<InputSource, string> = {
  http_requests: 'HTTP Requests',
  cli_args: 'CLI Arguments',
  file_input: 'File Input',
  message_queue: 'Message Queue',
  database: 'Database',
  internal_api: 'Internal API Only',
};

export const AUTHENTICATION_MODEL_LABELS: Record<AuthenticationModel, string> = {
  none: 'None',
  basic: 'Basic Auth',
  session: 'Session/Cookie',
  token: 'JWT/Token',
  oauth: 'OAuth/OIDC',
  service_to_service: 'Service-to-Service',
  api_key: 'API Key',
};

export const DATA_SENSITIVITY_LABELS: Record<DataSensitivity, string> = {
  public: 'Public Data',
  internal: 'Internal Business Data',
  confidential: 'Confidential',
  pii: 'PII/Personal Data',
  financial: 'Financial/PCI',
  healthcare: 'Healthcare/HIPAA',
};

// ===== Application Model =====

export interface Application {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  repositories: ApplicationRepository[];
  profile?: ApplicationProfile;
  created_by: string;
  created_at: string;
  updated_at: string;
  archived?: boolean;
}

export interface FindingsSeveritySummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
}

export interface ApplicationResponse extends Application {
  repository_count: number;
  findings_summary?: FindingsSeveritySummary;
}

export interface ApplicationListResponse {
  applications: ApplicationResponse[];
  total: number;
}

export interface ApplicationCreate {
  name: string;
  description?: string;
  repositories: ApplicationRepository[];
}

export interface ApplicationUpdate {
  name?: string;
  description?: string;
  repositories?: ApplicationRepository[];
}

// ===== Public Scan Page Types =====

export type PublicScanStatus = 'passing' | 'failing' | 'unknown';

export interface PublicScanSeverityCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
}

export interface PublicScanFindingStatusCounts {
  open: number;
  needs_manual_review: number;
  resolved: number;
  ignored: number;
  false_positive: number;
  total: number;
}

export interface PublicScanEngineStatus {
  scan_type: string;
  status: string;
  commit_hash?: string | null;
  completed_at?: string | null;
  fresh: boolean;
  message?: string | null;
}

export interface PublicScanRepositoryStatus {
  repository: string;
  repository_url?: string | null;
  provider: string;
  branch: string;
  scans: PublicScanEngineStatus[];
}

export interface PublicScanPageSnapshot {
  status: PublicScanStatus;
  status_reason: string;
  evaluated_at: string;
  freshness_days: number;
  stale_at: string;
  required_scan_types: string[];
  actionable_findings_count: number;
  findings_by_severity: PublicScanSeverityCounts;
  findings_by_status: PublicScanFindingStatusCounts;
  repositories: PublicScanRepositoryStatus[];
}

export interface PublicScanPage {
  id: string;
  organization_id: string;
  application_id: string;
  project_slug: string;
  display_name: string;
  description?: string | null;
  is_public: boolean;
  snapshot?: PublicScanPageSnapshot | null;
  created_by: string;
  updated_by?: string | null;
  published_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PublicScanPageResponse {
  page: PublicScanPage;
  organization_slug: string;
  public_url: string;
  badge_url: string;
  badge_markdown: string;
}

export interface PublicScanProjectResponse {
  organization_slug: string;
  project_slug: string;
  display_name: string;
  description?: string | null;
  is_public: boolean;
  public_url: string;
  badge_url: string;
  snapshot: PublicScanPageSnapshot;
  published_at?: string | null;
  updated_at: string;
}

export interface PublicScanPageUpdate {
  project_slug?: string;
  display_name?: string;
  description?: string | null;
  is_public?: boolean;
}

// ===== Repository Sandbox Config Types =====

export interface RepoEnvironmentVariable {
  id: string;
  name: string;
  value?: string;
  is_encrypted: boolean;
  description?: string;
}

export interface RepoEnvironmentVariableCreate {
  name: string;
  value: string;
  is_encrypted: boolean;
  description?: string;
}

export interface RepoEnvironmentVariableUpdate {
  name?: string;
  value?: string;
  is_encrypted?: boolean;
  description?: string;
}

export type NetworkAccessMode = 'deny_all' | 'allow_all' | 'custom';

export interface SandboxNetworkAccess {
  mode: NetworkAccessMode;
  allowed_hosts: string[];
}

export interface RepositorySandboxConfig {
  id: string;
  application_id: string;
  repository_full_name: string;
  environment_variables: RepoEnvironmentVariable[];
  setup_script?: string;
  network_access: SandboxNetworkAccess;
  created_at?: string;
  updated_at?: string;
}

export interface SetupScriptUpdate {
  setup_script?: string;
}

export interface NetworkAccessUpdate {
  mode: NetworkAccessMode;
  allowed_hosts: string[];
}
